import { Page } from "../../../components/page-base";
// import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { isMobile } from "is-mobile";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import {
  BulkAction,
  BulkActionsBar,
} from "../../../components/datagrid/BulkActionsBar";
import { EmailCopyCell } from "../../../components/datagrid/EmailCopyCell";
import { NameCell } from "../../../components/datagrid/NameCell";
import { RowActionButtons } from "../../../components/datagrid/RowActionButtons";
import { Status, StatusChip } from "../../../components/datagrid/StatusChip";
import { TrendIndicator } from "../../../components/datagrid/TrendIndicator";
import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import {
  ChipFilterConfig,
  FilterDrawer,
  SearchFilterConfig,
} from "../../../components/filter-drawer";
import { useEditProfileModal } from "../../../contexts/useEditProfileModal";
import useUserStore from "../../../store/user";
import { ProfileDetailsDialog } from "../../following-profiles/components/profile-details-modal";
import {
  EditProfileModal,
  type Profile,
} from "../../profiles-tracking/components/edit-profile-modal";
import { profilesTrackingService } from "../../profiles-tracking/services";
import { CustomersService } from "../services";
import { Container } from "./styles";

export interface Client {
  id: string;
  name: string;
  secondaryName?: string;
  location: string;
  email: string;
  lastUpdate: string;
  status: Status;
  statusLabel: string;
  rankedValue: number;
  businessProfileId: number;
  totalKw: number; // mantido apenas no mock
  allowAiCommentResponse: boolean;
}

export type TClient = {
  razaoSocial: string;
  updatedAt: Date;
  locatidade: string;
  email: string;
  status: string;
  profileBusinessId: string;
  cnpjCpf: string;
  telefone: string;
  enderecoComercial: string;
  rankedKeywords: 0;
  totalKeywords: 0;
  businessProfileId: number;
  suspendedAiReviewReplies: boolean;
  suspendedAiReviewRepliesLockedUntil: string | null;
};

const initialFilters = {
  status: "all",
  ranked: "all",
  location: "",
};

export const Clients = () => {
  const { user } = useUserStore();

  const [clients, setClients] = useState<Client[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Client | null>(null);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const {
    isOpen: isEditModalOpen,
    openModal,
    closeModal: closeEditModal,
  } = useEditProfileModal();
  const [loading, setLoading] = useState(true);
  const [appliedLocation, setAppliedLocation] = useState<string>("");
  const [submittedSearch, setSubmittedSearch] = useState("");

  type SortDirection = "asc" | "desc" | null;
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSortRankedKeywords = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? "asc" : prev === "desc" ? "asc" : null,
    );
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "LOGIN_PENDING":
        return "Aguardando login";

      case "ACTIVE":
        return "Ativo";

      case "PENDING_SYNC_LEGACY_KEYWORDS":
        return "Sincronização pendente";

      case "WAITING_FIRST_PAYMENT":
        return "Aguardando primeiro pagamento";

      default:
        return "Status desconhecido";
    }
  };

  type StatusColorKey = "success" | "warning" | "danger" | "disabled";

  const getStatusColorKey = useCallback((status: string): StatusColorKey => {
    switch (status) {
      case "ACTIVE":
        return "success";

      case "LOGIN_PENDING":
        return "warning";

      case "WAITING_FIRST_PAYMENT":
        return "danger";

      case "PENDING_SYNC_LEGACY_KEYWORDS":
        return "disabled";

      default:
        return "disabled";
    }
  }, []);

  // Efeito para buscar dados sempre que a paginação, filtros ou busca mudarem
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.token) {
        toast.warn("Sessão expirada. Faça login novamente.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const params = {
          token: user.token,
          page: pagination.page - 1,
          size: pagination.pageSize,
          query: submittedSearch,
          status: filters.status === "all" ? undefined : filters.status,
          ranking: filters.ranked === "all" ? undefined : filters.ranked,
          localidade: appliedLocation || undefined,
          ...(sortDirection && { sort: `${sortDirection}` }),
        };

        const response = await CustomersService.getCustomers(params);
        if (response && response.content) {
          const mappedProfiles: Client[] = response.content.map(
            (p: TClient) => ({
              id: p.profileBusinessId,
              name: p.razaoSocial,
              location: p.locatidade,
              email: p.email,
              allowAiCommentResponse: !p.suspendedAiReviewReplies,
              status: getStatusColorKey(p.status),
              statusLabel: getStatusLabel(p.status),
              lastUpdate: p.updatedAt
                ? new Date(p.updatedAt).toLocaleDateString("pt-BR")
                : "-",
              businessProfileId: p.businessProfileId,
              rankedValue: p.rankedKeywords,
              totalKw: p.totalKeywords,
            }),
          );
          setClients(mappedProfiles);
          setPagination((prev) => ({
            ...prev,
            totalCount: response.totalElements,
          }));
        } else {
          setClients([]);
          setPagination((prev) => ({ ...prev, totalCount: 0 }));
        }
      } catch (error) {
        console.error("Erro ao buscar perfis:", error);
        toast.error("Falha ao carregar perfis. Tente novamente.");
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    pagination.page,
    pagination.pageSize,
    filters.status,
    filters.ranked,
    appliedLocation,
    submittedSearch,
    user?.token,
    getStatusColorKey,
    sortDirection,
  ]);

  const handleEdit = (id: string) => {
    // find the client
    const client = clients.find((c) => c.id === id);
    if (!client) return;

    // build a profile minimal object that EditProfileModal expects
    const profileObj: Profile = {
      id: client.id,
      name: client.name,
      category: "",
      status: "preparing",
      stats: {
        views: 0,
        clicks: 0,
        ranking: client.rankedValue || 0,
      },
      email: client.email,
      address: client.location,
    };

    setEditingProfile(profileObj);
    openModal();
  };

  const handleNameClick = (profile: Client) => {
    setSelectedProfile(profile);
    setDetailsOpen(true);
  };

  const handleSelectRow = (rowId: string | number) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRowIds(checked ? clients.map((p) => p.id) : []);
  };

  const exportFile = () => {
    // Filtra os clientes com base nos IDs selecionados
    const selectedClients = clients.filter((client) =>
      selectedRowIds.includes(client.id),
    );

    if (selectedClients.length === 0) {
      return; // Não faz nada se não houver linhas selecionadas
    }

    // Cria uma planilha a partir dos dados JSON
    const worksheet = XLSX.utils.json_to_sheet(selectedClients);
    // Cria um novo workbook
    const workbook = XLSX.utils.book_new();
    // Adiciona a planilha ao workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clientes");
    // Gera o arquivo XLSX e inicia o download
    XLSX.writeFile(workbook, "clientes_selecionados.xlsx");

    // Limpa a seleção após a exportação
    setSelectedRowIds([]);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    // Aqui você chamaria a API para buscar os dados filtrados
    console.log(`Filtering by ${field}: ${value}`);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setAppliedLocation("");

    // Aqui você chamaria a API para buscar os dados sem filtros
    console.log("Clearing filters");
  };

  const handleSearchLocation = (query: string) => {
    // Aqui você chamaria a API para buscar por localidade
    setAppliedLocation(query);
    console.log("Searching location in DB:", query);
  };

  const chipFilters: ChipFilterConfig[] = [
    {
      id: "status",
      label: "Status",
      options: [
        { label: "Todos", value: "all" },
        { label: "Ativo", value: "ACTIVE" },
        { label: "Aguardando login", value: "LOGIN_PENDING" },
        {
          label: "Aguardando primeiro pagamento",
          value: "WAITING_FIRST_PAYMENT",
        },
        {
          label: "Sincronização pendente",
          value: "PENDING_SYNC_LEGACY_KEYWORDS",
        },
      ],
    },
    {
      id: "ranked",
      label: "Palavras Ranqueadas",
      options: [
        { label: "Todas", value: "all" },
        { label: "Zero", value: "zero" },
        { label: "1-7", value: "1-7" },
        { label: "8+", value: "8+" },
      ],
    },
  ];

  const searchFilters: SearchFilterConfig[] = [
    {
      id: "location",
      label: "Localidade",
      placeholder: "Buscar por localidade",
      onSearch: handleSearchLocation,
    },
  ];

  // Colunas (Ações à direita, remove Total KW e Acompanhamento, renomeia atualização)
  const handleViewDashboard = (profileId: string) => {
    window.open(`/dashboard/${profileId}`, "_blank");
  };

  const columns: DataGridColumn[] = [
    { id: "name", header: "Nome", minWidth: 220 },
    { id: "location", header: "Localidade", minWidth: 150 },
    { id: "email", header: "Email", minWidth: 240 },
    { id: "status", header: "Status", minWidth: 100 },
    {
      id: "lastUpdate",
      header: "Atualizada em",
      minWidth: 140,
      hideOnMobile: true,
    },
    {
      id: "ranked",
      minWidth: 160,
      header: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Palavras Ranqueadas
          <Tooltip
            title={
              sortDirection === null
                ? "Ordenar decrescente"
                : sortDirection === "desc"
                  ? "Ordenar crescente"
                  : "Remover ordenação"
            }
          >
            <IconButton size="small" onClick={handleSortRankedKeywords}>
              {sortDirection === "desc" ? (
                <ArrowDownwardIcon fontSize="inherit" />
              ) : sortDirection === "asc" ? (
                <ArrowUpwardIcon fontSize="inherit" />
              ) : (
                <UnfoldMoreIcon fontSize="inherit" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { id: "actions", header: "Ações", minWidth: 100 },
  ];

  const rows: DataGridRow[] = useMemo(() => {
    return clients.map((profile) => ({
      id: profile.id,
      render: {
        name: (
          <NameCell
            primaryText={profile.name}
            onClick={() => handleNameClick(profile)}
          />
        ),
        location: (
          <Typography variant="body2" color="text.secondary">
            {profile.location}
          </Typography>
        ),
        email: <EmailCopyCell email={profile.email} />,
        status: (
          <StatusChip status={profile.status} label={profile.statusLabel} />
        ),
        lastUpdate: (
          <Typography variant="body2" color="text.secondary">
            {profile.lastUpdate}
          </Typography>
        ),
        ranked: <TrendIndicator value={profile.rankedValue} />,
        actions: (
          <RowActionButtons
            actions={[
              {
                icon: <VisibilityOutlinedIcon fontSize="small" />,
                tooltip: "Ver Dashboard",
                onClick: () => handleViewDashboard(profile.id),
              },
              // {
              //   icon: <ModeEditOutlineIcon fontSizstringsmall" />,
              //   tooltip: "Editar",
              //   onClick: () => handleEdit(profile.id),
              // },
            ]}
          />
        ),
      },
    }));
  }, [clients]);

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    // Helper para encontrar o label de uma opção de filtro
    const getOptionLabel = (filterId: string, value: any) => {
      const filterConfig = chipFilters.find((f) => f.id === filterId);
      const option = filterConfig?.options.find((o) => o.value === value);
      return option?.label ?? value;
    };

    if (filters.status && filters.status !== "all") {
      chips.push({
        id: "status",
        label: `Status: ${getOptionLabel("status", filters.status)}`,
        onDelete: () => handleFilterChange("status", "all"),
      });
    }
    if (filters.ranked && filters.ranked !== "all") {
      chips.push({
        id: "ranked",
        label: `Ranqueadas: ${getOptionLabel("ranked", filters.ranked)}`,
        onDelete: () => handleFilterChange("ranked", "all"),
      });
    }
    if (appliedLocation) {
      chips.push({
        id: "location",
        label: `Local: ${appliedLocation}`,
        onDelete: () => {
          handleFilterChange("location", "");
          setAppliedLocation("");
        },
      });
    }
    return chips;
  }, [filters, appliedLocation, chipFilters]);

  const handleSearchClick = useCallback(() => {
    // aplica a busca e volta para a primeira página
    setPagination((p) => ({ ...p, page: 1 }));
    setSubmittedSearch(searchTerm);
  }, [searchTerm]);

  const handleSaveProfile = async (updatedProfile: Profile) => {
    console.log("CLIENTS handleSaveProfile called");
    try {
      setLoading(true);
      await profilesTrackingService.updateProfile(
        updatedProfile.id,
        updatedProfile,
      );

      // Update clients list with some info returned
      setClients((prev) =>
        prev.map((c) =>
          c.id === updatedProfile.id
            ? {
                ...c,
                name: updatedProfile.name || c.name,
                email: updatedProfile.email || c.email,
                location:
                  updatedProfile.city || updatedProfile.address || c.location,
                rankedValue: updatedProfile.stats?.ranking ?? c.rankedValue,
              }
            : c,
        ),
      );

      toast.success("Perfil atualizado com sucesso!");
      // Close modal and clear editing profile
      closeEditModal();
      setEditingProfile(null);
    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      toast.error("Erro ao atualizar perfil");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Perfis Ativos"
          subtitle="Todos os perfis conectados à API do Google"
        />
        <Box
          sx={{ width: "88%", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}
        >
          <AppDataGrid
            columns={columns}
            rows={rows}
            tableLabel="perfis"
            selectedRowIds={selectedRowIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar por nome, localidade ou email..." // parâmetro
            filterChips={filterChips} // chips ativos
            filterCount={filterChips.length} // contador no botão
            onSearchClick={handleSearchClick}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            bulkActionsBarRenderer={(selectedIds) => {
              const bulkActions: BulkAction[] = [
                {
                  label: "Exportar",
                  onClick: exportFile,
                },
                {
                  label: "Cancelar",
                  onClick: () => setSelectedRowIds([]),
                  color: "secondary",
                },
              ];

              return (
                <BulkActionsBar
                  selectedCount={selectedIds.length}
                  tableLabel="perfil"
                  actions={bulkActions}
                />
              );
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhum perfil em acompanhamento"
            noDataDescription="Adicione perfis para começar o monitoramento"
            isLoading={loading}
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          chipFilters={chipFilters}
          searchFilters={searchFilters}
        />
        <ProfileDetailsDialog
          open={detailsOpen}
          /* eslint-disable-next-line */
          /* @ts-ignore */
          profile={
            selectedProfile
              ? {
                  id: selectedProfile.id,
                  name: selectedProfile.name,
                  location: selectedProfile.location,
                  email: selectedProfile.email,
                  totalKw: selectedProfile.totalKw,
                  rankedValue: selectedProfile.rankedValue,
                  status: selectedProfile.status,
                  statusLabel: selectedProfile.statusLabel,
                  lastUpdate: selectedProfile.lastUpdate,
                }
              : null
          }
          onClose={() => setDetailsOpen(false)}
          onEdit={(id: string | number) => handleEdit(String(id))}
          onViewDetails={(id: string | number) =>
            handleViewDashboard(String(id))
          }
        />
        <EditProfileModal
          isOpen={isEditModalOpen}
          profile={editingProfile}
          onClose={() => {
            closeEditModal();
            setEditingProfile(null);
          }}
          onSave={handleSaveProfile}
        />
      </Container>
    </Page.Root>
  );
};
